import { Head, Link, router } from "@inertiajs/react"
import { Box, Button, Paper, Stack, Typography } from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayer from "../../../components/AudioPlayer"

type Translation = {
  id: number
  language: string
  text: string
  hint: string | null
  position: number
}

type Audio = { url: string; language: string; ai_model: string; voice: string }

type Word = {
  id: number
  kanji: string
  kana: string
  romaji: string | null
  romaji_obvious: boolean
  translations: Translation[]
  audio: Audio | null
  topic_ids: number[]
  sentences: { id: number; kanji: string; kana: string }[]
}

const LANGUAGE_LABELS: Record<string, string> = { en: "English", de: "Deutsch" }

function TranslationsList({ translations }: { translations: Translation[] }) {
  if (translations.length === 0) return <Typography>—</Typography>
  const byLang: Record<string, Translation[]> = {}
  translations.forEach((t) => {
    byLang[t.language] ??= []
    byLang[t.language].push(t)
  })
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {Object.keys(byLang).map((lang) => (
        <Box key={lang} data-testid={`translations-${lang}`}>
          <Typography variant="subtitle2">{LANGUAGE_LABELS[lang] ?? lang}</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {byLang[lang].map((t) => (
              <Box key={t.id}>
                <Typography>{t.text}</Typography>
                {t.hint && (
                  <Typography variant="body2" color="text.secondary">Hint: {t.hint}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default function ShowWord({ word }: { word: Word }) {
  const remove = () => {
    if (confirm("Delete this word?")) router.delete(`/admin/words/${word.id}`)
  }

  return (
    <AdminLayout title={word.kanji || word.kana}>
      <Head title={word.kanji || word.kana} />
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">{word.kanji || word.kana}</Typography>
        <Stack direction="row" gap={1}>
          <Button component={Link} href={`/admin/words/${word.id}/edit`}>Edit</Button>
          <Button color="error" onClick={remove}>Delete</Button>
        </Stack>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, alignItems: "start" }}>
          <Typography color="text.secondary">Kanji</Typography><Typography>{word.kanji || "—"}</Typography>
          <Typography color="text.secondary">Kana</Typography><Typography>{word.kana || "—"}</Typography>
          <Typography color="text.secondary">Romaji</Typography>
          <Typography>{word.romaji || "—"}{word.romaji_obvious ? " (obvious)" : ""}</Typography>
          <Typography color="text.secondary">Translations</Typography>
          <TranslationsList translations={word.translations} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Audio</Typography>
          <AudioPlayer src={word.audio?.url ?? null} />
          {word.audio && (
            <Typography variant="caption" color="text.secondary">
              {word.audio.language} · {word.audio.ai_model} · {word.audio.voice}
            </Typography>
          )}
        </Box>
      </Paper>
      <Typography variant="h6" sx={{ mt: 3 }}>Sentences using this word ({word.sentences.length})</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {word.sentences.map((s) => (
          <Link key={s.id} href={`/admin/sentences/${s.id}`}>{s.kanji || s.kana}</Link>
        ))}
      </Box>
    </AdminLayout>
  )
}
